// SVG charts for shared dashboards (/d/<token>). Kept apart from
// lib/metrics/charts.js on purpose: these draw in the account's brand (one
// dark data colour plus the account accent, read from CSS variables on the
// page) instead of the Metrics palette. Each function fills `el` with an SVG
// and registers hover text through `tip(html)`, which returns the attribute
// to put on a hover target.

const DAY_LABEL = { month: 'short', day: 'numeric', timeZone: 'UTC' }

export function fmtDay(iso) {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-US', DAY_LABEL)
}

export function days1(x) {
  if (x == null) return 'n/a'
  return x < 10 ? Number(x).toFixed(1) : String(Math.round(x))
}

export function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]))
}

export function colors(el) {
  const css = getComputedStyle(el)
  const v = name => css.getPropertyValue(name).trim()
  return {
    mark: v('--sd-mark'),
    accent: v('--sd-accent'),
    rule: v('--sd-rule'),
    muted: v('--sd-muted'),
    ink: v('--sd-ink'),
    ink2: v('--sd-ink-2')
  }
}

function niceTicks(max, count, integer) {
  let top = max > 0 ? max : 1
  const raw = top / count
  const mag = 10 ** Math.floor(Math.log10(raw))
  let step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= raw)
  if (integer) step = Math.max(1, Math.ceil(step))
  top = Math.ceil(top / step - 1e-9) * step
  const out = []
  for (let v = 0; v <= top + 1e-9; v += step) out.push(+v.toFixed(4))
  return out
}

// Column with a rounded top, square at the baseline.
function colPath(x, y, w, h, r) {
  if (h <= 0) return ''
  const rr = Math.min(r, h, w / 2)
  return `M${x},${y + h}v${-(h - rr)}a${rr},${rr} 0 0 1 ${rr},${-rr}h${w - 2 * rr}a${rr},${rr} 0 0 1 ${rr},${rr}v${h - rr}z`
}

// Bar with a rounded right end, square at the baseline.
function barPath(x, y, w, h, r) {
  if (w <= 0) return ''
  const rr = Math.min(r, w, h / 2)
  return `M${x},${y}h${w - rr}a${rr},${rr} 0 0 1 ${rr},${rr}v${h - 2 * rr}a${rr},${rr} 0 0 1 ${-rr},${rr}h${-(w - rr)}z`
}

function fill(el, html) {
  el.replaceChildren()
  el.insertAdjacentHTML('afterbegin', html)
}

function svg(el, w, h, label, body) {
  fill(el, `<svg viewBox="0 0 ${w} ${h}" height="${h}" role="img" aria-label="${esc(label)}">${body}</svg>`)
}

export function empty(el, message) {
  fill(el, `<p class="sd-empty">${esc(message)}</p>`)
}

export function sparkline(values, color) {
  const W = 96; const H = 30
  const known = values.filter(v => v != null)
  if (known.length < 2) return ''
  const max = Math.max(...known); const min = Math.min(0, ...known)
  const x = i => (values.length < 2 ? W / 2 : (i * (W - 6)) / (values.length - 1) + 3)
  const y = v => H - 4 - ((H - 8) * (v - min)) / ((max - min) || 1)
  let d = ''; let first = null; let last = null
  values.forEach((v, i) => {
    if (v == null) return
    d += `${d ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`
    if (first == null) first = x(i)
    last = [x(i), y(v)]
  })
  const area = `${d}L${last[0].toFixed(1)},${H}L${first.toFixed(1)},${H}Z`
  return `<svg class="sd-spark" viewBox="0 0 ${W} ${H}" aria-hidden="true"><path d="${area}" fill="${color}" fill-opacity="0.09"/>`
    + `<path d="${d}" fill="none" stroke="${color}" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>`
    + `<circle cx="${last[0]}" cy="${last[1]}" r="3" fill="${color}" stroke="white" stroke-width="1.5"/></svg>`
}

// Weekly median lead time (line) over jobs completed per week (columns): two
// panels sharing one x axis, never a second y scale.
export function leadTrend(el, weeks, c, tip) {
  const W = Math.max(300, el.clientWidth)
  const padL = 34; const padR = 56; const top1 = 22; const h1 = 150; const gap = 40; const h2 = 70; const axisH = 22
  const top2 = top1 + h1 + gap; const H = top2 + h2 + axisH
  const band = (W - padL - padR) / weeks.length
  const cx = i => padL + band * (i + 0.5)
  const yT = niceTicks(Math.max(1, ...weeks.map(w => w.median_lead_days || 0)), 4)
  const y1 = v => top1 + h1 - (h1 * v) / yT[yT.length - 1]
  const nT = niceTicks(Math.max(1, ...weeks.map(w => w.completed)), 2, true)
  const y2 = v => top2 + h2 - (h2 * v) / nT[nT.length - 1]
  const num = 'style="font-variant-numeric:tabular-nums"'
  let s = ''

  weeks.forEach((w, i) => {
    const med = w.median_lead_days == null ? 'no jobs completed' : `${days1(w.median_lead_days)} days`
    s += `<rect class="sd-hov" x="${padL + band * i}" y="${top1 - 6}" width="${band}" height="${top2 + h2 - top1 + 6}" ${tip(
      `<b>Week of ${fmtDay(w.week)}</b><span class="sd-k">Median lead time</span> ${med}<br><span class="sd-k">Jobs completed</span> ${w.completed}`
    )}/>`
  })
  s += `<text x="${padL}" y="${top1 - 10}" font-size="12" font-weight="600" fill="${c.ink2}">Median lead time, days</text>`
  yT.forEach((v) => {
    s += `<line x1="${padL}" x2="${W - padR}" y1="${y1(v)}" y2="${y1(v)}" stroke="${c.rule}"/>`
      + `<text x="${padL - 8}" y="${y1(v) + 4}" text-anchor="end" font-size="11" fill="${c.muted}" ${num}>${v}</text>`
  })

  const segs = []; let seg = []
  weeks.forEach((w, i) => {
    if (w.median_lead_days == null) { if (seg.length) segs.push(seg); seg = [] } else seg.push([cx(i), y1(w.median_lead_days)])
  })
  if (seg.length) segs.push(seg)
  segs.forEach((p) => {
    const d = p.map((q, k) => `${k ? 'L' : 'M'}${q[0].toFixed(1)},${q[1].toFixed(1)}`).join('')
    if (p.length > 1) {
      s += `<path d="${d}L${p[p.length - 1][0].toFixed(1)},${top1 + h1}L${p[0][0].toFixed(1)},${top1 + h1}Z" fill="${c.mark}" fill-opacity="0.08" pointer-events="none"/>`
    }
    s += `<path d="${d}" fill="none" stroke="${c.mark}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" pointer-events="none"/>`
    if (p.length === 1) s += `<circle cx="${p[0][0]}" cy="${p[0][1]}" r="3" fill="${c.mark}" pointer-events="none"/>`
  })
  let lastI = -1
  weeks.forEach((w, i) => { if (w.median_lead_days != null) lastI = i })
  if (lastI >= 0) {
    const ly = y1(weeks[lastI].median_lead_days)
    s += `<circle cx="${cx(lastI)}" cy="${ly}" r="4.5" fill="${c.mark}" stroke="white" stroke-width="2" pointer-events="none"/>`
      + `<text x="${cx(lastI) + 10}" y="${ly + 4}" font-size="12" font-weight="600" fill="${c.ink}" pointer-events="none">${days1(weeks[lastI].median_lead_days)} d</text>`
  }

  s += `<text x="${padL}" y="${top2 - 10}" font-size="12" font-weight="600" fill="${c.ink2}">Jobs completed</text>`
  nT.forEach((v) => {
    s += `<line x1="${padL}" x2="${W - padR}" y1="${y2(v)}" y2="${y2(v)}" stroke="${c.rule}"/>`
      + `<text x="${padL - 8}" y="${y2(v) + 4}" text-anchor="end" font-size="11" fill="${c.muted}" ${num}>${v}</text>`
  })
  const bw = Math.max(2, Math.min(24, band * 0.62))
  weeks.forEach((w, i) => {
    if (w.completed) s += `<path d="${colPath(cx(i) - bw / 2, y2(w.completed), bw, top2 + h2 - y2(w.completed), 3)}" fill="${c.mark}" pointer-events="none"/>`
  })
  const every = Math.max(1, Math.ceil(weeks.length / Math.max(2, Math.floor((W - padL - padR) / 64))))
  weeks.forEach((w, i) => {
    if (i % every === 0) s += `<text x="${cx(i)}" y="${top2 + h2 + 16}" text-anchor="middle" font-size="11" fill="${c.muted}">${fmtDay(w.week)}</text>`
  })
  svg(el, W, H, 'Weekly median lead time and jobs completed', s)
}

// Status-to-status spans in 3-day bins (Metrics::JobsQueries#status_span),
// the last bin open-ended, with the median marked in the accent.
export function spanBins(el, bins, median, c, tip) {
  const W = Math.max(260, el.clientWidth); const H = 132; const top = 22; const axisH = 20; const h = H - top - axisH
  const maxB = Math.max(1, ...bins.map(b => b.jobs))
  const band = W / bins.length; const bw = Math.min(24, band - 2)
  let s = `<line x1="0" x2="${W}" y1="${top + h}" y2="${top + h}" stroke="${c.rule}"/>`
  bins.forEach((b, i) => {
    const x = band * i
    const label = b.to_days == null ? `${b.from_days} days or more` : `${b.from_days} to ${b.to_days} days`
    s += `<rect class="sd-hov" x="${x}" y="${top}" width="${band}" height="${h}" ${tip(`<b>${label}</b>${b.jobs} job${b.jobs === 1 ? '' : 's'}`)}/>`
    if (b.jobs) {
      const bh = (h * b.jobs) / maxB
      s += `<path d="${colPath(x + (band - bw) / 2, top + h - bh, bw, bh, 3)}" fill="${c.mark}" pointer-events="none"/>`
    }
  })
  const every = Math.max(1, Math.ceil(bins.length / 6))
  bins.forEach((b, i) => {
    if (i % every === 0) s += `<text x="${band * i}" y="${H - 4}" font-size="11" fill="${c.muted}">${b.from_days}${b.to_days == null ? '+' : ''}</text>`
  })
  s += `<text x="${W}" y="${H - 4}" font-size="11" fill="${c.muted}" text-anchor="end">days</text>`
  if (median != null) {
    const step = bins[0].to_days - bins[0].from_days
    const mx = Math.min(W, band * (median / step))
    const right = mx > W - 90
    s += `<line x1="${mx}" x2="${mx}" y1="${top - 4}" y2="${top + h}" stroke="${c.accent}" stroke-width="3" pointer-events="none"/>`
      + `<text x="${mx + (right ? -6 : 6)}" y="${top - 8}" font-size="11" font-weight="600" fill="${c.ink}" text-anchor="${right ? 'end' : 'start'}" pointer-events="none">Median ${days1(median)} d</text>`
  }
  svg(el, W, H, 'Days between the two statuses', s)
}

// One row: a median-days bar on a shared scale.
export function statusBar(el, row, xMax, ticks, c, tip) {
  const W = Math.max(160, el.clientWidth); const H = 40; const padR = 54; const bh = 16; const y = 4
  const sx = v => ((W - padR) * v) / xMax
  let s = ''
  ticks.forEach((v) => {
    s += `<line x1="${sx(v)}" x2="${sx(v)}" y1="0" y2="${y + bh + 4}" stroke="${c.rule}"/>`
      + `<text x="${sx(v)}" y="${H - 2}" font-size="10.5" fill="${c.muted}" text-anchor="middle">${v}${v === xMax ? ' d' : ''}</text>`
  })
  s += `<rect class="sd-hov" x="0" y="0" width="${W}" height="${y + bh + 4}" ${tip(
    `<b>${esc(row.status)}</b><span class="sd-k">Median</span> ${days1(row.median_days)} days<br><span class="sd-k">Jobs</span> ${row.jobs}`
  )}/>`
  s += `<path d="${barPath(0, y, sx(row.median_days), bh, 4)}" fill="${c.mark}" pointer-events="none"/>`
    + `<text x="${sx(row.median_days) + 8}" y="${y + bh / 2 + 4}" font-size="12" font-weight="600" fill="${c.ink}" pointer-events="none">${days1(row.median_days)} d</text>`
  svg(el, W, H, `${row.status}: median ${days1(row.median_days)} days`, s)
}

export { niceTicks }

// Horizontal bars per person, done (completed or closed, dark) then still open (accent),
// total at the tip. Past `cap` people the rest fold into one "Other" row.
export function peopleBars(el, rows, cap, label, c, tip) {
  let list = rows
  const named = rows.filter(r => !r.none)
  const nobody = rows.filter(r => r.none)
  if (named.length > cap) {
    const rest = named.slice(cap - 1)
    list = named.slice(0, cap - 1).concat({
      name: `Other (${rest.length} people)`,
      other: true,
      completed: rest.reduce((t, r) => t + r.completed, 0),
      open: rest.reduce((t, r) => t + r.open, 0)
    }).concat(nobody)
  }
  const W = Math.max(280, el.clientWidth); const labelW = Math.min(150, W * 0.36); const padR = 40
  const rowH = 32; const bh = 16; const top = 2; const axisH = 20
  const H = top + list.length * rowH + axisH
  const ticks = niceTicks(Math.max(...list.map(r => r.completed + r.open)), 4, true)
  const xMax = ticks[ticks.length - 1]
  const sx = v => labelW + ((W - labelW - padR) * v) / xMax
  const num = 'style="font-variant-numeric:tabular-nums"'
  let s = ''
  ticks.forEach((v) => {
    s += `<line x1="${sx(v)}" x2="${sx(v)}" y1="${top}" y2="${top + list.length * rowH}" stroke="${c.rule}"/>`
      + `<text x="${sx(v)}" y="${H - 4}" text-anchor="middle" font-size="11" fill="${c.muted}" ${num}>${v}</text>`
  })
  list.forEach((r, i) => {
    const y = top + i * rowH; const by = y + (rowH - bh) / 2; const total = r.completed + r.open
    s += `<rect class="sd-hov" x="0" y="${y}" width="${W}" height="${rowH}" ${tip(
      `<b>${esc(r.name)}</b><span class="sd-k">Done</span> ${r.completed}<br><span class="sd-k">Still open</span> ${r.open}<br><span class="sd-k">Total</span> ${total}`
    )}/>`
    const name = r.name.length > 22 ? `${r.name.slice(0, 21)}…` : r.name
    s += `<text x="0" y="${y + rowH / 2 + 4}" font-size="13" fill="${r.other || r.none ? c.muted : c.ink2}" pointer-events="none">${esc(name)}</text>`
    const segs = [[r.completed, c.mark], [r.open, c.accent]].filter(q => q[0] > 0)
    let acc = 0
    segs.forEach(([v, col], k) => {
      const x0 = sx(acc) + (k ? 1 : 0); const x1 = sx(acc + v) - (k < segs.length - 1 ? 1 : 0)
      s += k === segs.length - 1
        ? `<path d="${barPath(x0, by, x1 - x0, bh, 4)}" fill="${col}" pointer-events="none"/>`
        : `<rect x="${x0}" y="${by}" width="${Math.max(0, x1 - x0)}" height="${bh}" fill="${col}" pointer-events="none"/>`
      acc += v
    })
    s += `<text x="${sx(total) + 6}" y="${y + rowH / 2 + 4}" font-size="12" font-weight="600" fill="${c.ink}" pointer-events="none" ${num}>${total}</text>`
  })
  svg(el, W, H, label, s)
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October',
  'November', 'December']

// 'YYYY-MM' as 'Mar 2026', or 'March 2026' when long.
export function monthLabel(key, long) {
  const names = long ? MONTH_FULL : MONTH_NAMES
  return `${names[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`
}

// 1,234 -> 1.2k, 24,014 -> 24k: short enough to sit on a column.
export function compact(v) {
  if (v < 1000) return String(v)
  if (v < 10000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`
  return `${Math.round(v / 1000)}k`
}

// Monthly columns, one series or a pair side by side (intake in the accent,
// completed in the dark mark), with the month still running drawn lighter.
// Values print on the columns when they fit; the hover band carries every
// number either way. A null value is a month with no record (no column, and
// "Not recorded" on hover). `extra(month)` adds lines to a month's hover text.
export function monthlyBars(el, months, series, c, tip, extra) {
  const W = Math.max(280, el.clientWidth); const H = 236
  const padL = 40; const padR = 4; const top = 18; const axisH = 36; const h = H - top - axisH
  const band = (W - padL - padR) / months.length
  const cx = i => padL + band * (i + 0.5)
  const ticks = niceTicks(Math.max(1, ...months.flatMap(m => series.map(sr => m[sr.key] || 0))), 4, true)
  const y = v => top + h - (h * v) / ticks[ticks.length - 1]
  const paired = series.length > 1
  const groupW = Math.min(band * (paired ? 0.74 : 0.6), paired ? 58 : 34)
  const bw = paired ? (groupW - 2) / 2 : groupW
  // Neighbouring labels in a pair sit bw + 2 apart; keep a few px between them.
  const textW = Math.max(...months.flatMap(m => series.map(sr => compact(m[sr.key] || 0).length))) * 6.1
  const labels = paired ? textW + 5 <= bw + 2 : textW + 6 <= band
  const num = 'style="font-variant-numeric:tabular-nums"'
  let s = ''

  ticks.forEach((v) => {
    s += `<line x1="${padL}" x2="${W - padR}" y1="${y(v)}" y2="${y(v)}" stroke="${c.rule}"/>`
      + `<text x="${padL - 8}" y="${y(v) + 4}" text-anchor="end" font-size="11" fill="${c.muted}" ${num}>${compact(v)}</text>`
  })
  months.forEach((m, i) => {
    const lines = series.map(sr => `<i style="background:${sr.color}"></i><span class="sd-k">${esc(sr.name)}</span> `
      + `${m[sr.key] == null ? 'Not recorded' : m[sr.key].toLocaleString('en-US')}`)
    const sofar = m.partial ? '<br><span class="sd-k">This month so far</span>' : ''
    s += `<rect class="sd-hov" x="${padL + band * i}" y="${top - 8}" width="${band}" height="${h + 8}" ${tip(
      `<b>${monthLabel(m.month, true)}</b>${lines.join('<br>')}${extra ? extra(m) : ''}${sofar}`
    )}/>`
  })
  months.forEach((m, i) => {
    const x0 = cx(i) - groupW / 2
    series.forEach((sr, k) => {
      const v = m[sr.key]; const x = x0 + k * (bw + 2)
      if (!v) return
      s += `<path d="${colPath(x, y(v), bw, top + h - y(v), 3)}" fill="${sr.color}" fill-opacity="${m.partial ? 0.45 : 1}" pointer-events="none"/>`
      if (labels) s += `<text x="${x + bw / 2}" y="${y(v) - 5}" text-anchor="middle" font-size="10.5" fill="${c.ink2}" pointer-events="none" ${num}>${compact(v)}</text>`
    })
  })
  const every = Math.max(1, Math.ceil(months.length / Math.max(2, Math.floor((W - padL - padR) / 34))))
  months.forEach((m, i) => {
    const month = Number(m.month.slice(5, 7))
    if (i % every === 0) s += `<text x="${cx(i)}" y="${top + h + 16}" text-anchor="middle" font-size="11" fill="${c.muted}">${MONTH_NAMES[month - 1]}</text>`
    if (i === 0 || month === 1) s += `<text x="${cx(i)}" y="${top + h + 31}" text-anchor="middle" font-size="11" font-weight="600" fill="${c.ink2}">${m.month.slice(0, 4)}</text>`
  })
  s += `<line x1="${padL}" x2="${W - padR}" y1="${top + h}" y2="${top + h}" stroke="${c.muted}" stroke-opacity="0.5"/>`
  svg(el, W, H, `${series.map(sr => sr.name).join(' and ')} by month`, s)
}

// Shows the hover text of the [data-sd-tip] under the pointer inside `root`
// (tips[index]), kept on screen; hides it anywhere else.
export function placeTip(e, root, tipEl, tips) {
  const target = e.target.closest && e.target.closest('[data-sd-tip]')
  if (!target || !root.contains(target)) { tipEl.toggleAttribute('hidden', true); return }
  fill(tipEl, tips[Number(target.getAttribute('data-sd-tip'))] || '')
  tipEl.toggleAttribute('hidden', false)
  const r = tipEl.getBoundingClientRect()
  let x = e.clientX + 14; let yy = e.clientY + 14
  if (x + r.width > window.innerWidth - 8) x = e.clientX - r.width - 14
  if (yy + r.height > window.innerHeight - 8) yy = e.clientY - r.height - 14
  tipEl.style.setProperty('left', `${x}px`)
  tipEl.style.setProperty('top', `${yy}px`)
}
